const EDGE_GAP_PX = 8;
const MIN_VISIBLE_PX = 24;
/** Match TourKit hotspot corner inset (`top/left = edge ± 4`). */
const CORNER_INSET_PX = 4;
/** Gap when parking the chip outside the target (above / beside). */
const OUTSIDE_GAP_PX = 6;
/** Fallback until the beacon mounts and we can measure it. */
const DEFAULT_BEACON_WIDTH_PX = 72;
const DEFAULT_BEACON_HEIGHT_PX = 32;

export type SafeAreaInsets = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

let cachedInsets: SafeAreaInsets | null = null;
let insetListenersBound = false;

function readSafeAreaInsetsUncached(): SafeAreaInsets {
  if (typeof document === 'undefined') {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;pointer-events:none;visibility:hidden;padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)';
  document.documentElement.append(probe);
  const style = getComputedStyle(probe);
  const insets = {
    top: Number.parseFloat(style.paddingTop) || 0,
    right: Number.parseFloat(style.paddingRight) || 0,
    bottom: Number.parseFloat(style.paddingBottom) || 0,
    left: Number.parseFloat(style.paddingLeft) || 0,
  };
  probe.remove();
  return insets;
}

function bindInsetCacheInvalidation() {
  if (insetListenersBound || typeof window === 'undefined') return;
  insetListenersBound = true;
  const invalidate = () => {
    cachedInsets = null;
  };
  window.addEventListener('resize', invalidate, { passive: true });
  window.visualViewport?.addEventListener('resize', invalidate);
  window.visualViewport?.addEventListener('scroll', invalidate);
  // Orientation / Cap safe-area can change without a layout resize event.
  window.addEventListener('orientationchange', invalidate, { passive: true });
}

/** Cached safe-area insets — avoid DOM probes on every scroll frame. */
export function getSafeAreaInsets(): SafeAreaInsets {
  bindInsetCacheInvalidation();
  if (!cachedInsets) {
    cachedInsets = readSafeAreaInsetsUncached();
  }
  return cachedInsets;
}

/** Test helper — clears the inset cache. */
export function resetSafeAreaInsetCacheForTests(): void {
  cachedInsets = null;
}

/**
 * Keep TourKit's target rect on-screen (status bar + rounded iPhone edge).
 * Returns null when the target has scrolled/overscrolled away.
 */
export function clampHintAnchorRect(rect: DOMRect): DOMRect | null {
  if (typeof window === 'undefined') return rect;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (
    rect.bottom < MIN_VISIBLE_PX ||
    rect.top > vh - MIN_VISIBLE_PX ||
    rect.right < MIN_VISIBLE_PX ||
    rect.left > vw - MIN_VISIBLE_PX
  ) {
    return null;
  }

  const safe = getSafeAreaInsets();
  const minTop = safe.top + EDGE_GAP_PX;
  const minLeft = safe.left + EDGE_GAP_PX;
  const maxRight = vw - safe.right - EDGE_GAP_PX;
  const maxBottom = vh - safe.bottom - EDGE_GAP_PX;

  const top = Math.max(rect.top, minTop);
  const left = Math.max(rect.left, minLeft);
  const right = Math.min(rect.right, maxRight);
  const bottom = Math.min(rect.bottom, maxBottom);
  const width = right - left;
  const height = bottom - top;
  if (width < 8 || height < 8) return null;

  return new DOMRect(left, top, width, height);
}

export type HintBeaconSize = {
  width: number;
  height: number;
};

export type HintBeaconPosition = {
  top: number;
  left: number;
};

/**
 * Final `position:fixed` top/left for a tip chip that must stay fully inside
 * the safe viewport — without CSS `translate()` fighting TourKit.
 *
 * Prefers sitting *outside* the target (above-right, right, below-right) so
 * full-width CTAs stay readable, then the classic top-right inset. A spot for
 * which `isBlocked` reports another control underneath is skipped (DEV-579:
 * the chip covered the sort select and the copy button); if every spot is
 * blocked the first one that fits wins.
 *
 * Hidden (null) once the target's top edge has scrolled out above the
 * viewport — clamping it to the top used to park the chip over whatever
 * scrolled under it (DEV-579).
 */
export function computeHintBeaconPosition(
  targetRect: DOMRect,
  size: HintBeaconSize = {
    width: DEFAULT_BEACON_WIDTH_PX,
    height: DEFAULT_BEACON_HEIGHT_PX,
  },
  isBlocked?: (candidate: DOMRect) => boolean,
): HintBeaconPosition | null {
  if (typeof window === 'undefined') {
    return {
      top: targetRect.top - size.height - OUTSIDE_GAP_PX,
      left: targetRect.right - size.width,
    };
  }

  const clamped = clampHintAnchorRect(targetRect);
  if (!clamped) return null;

  const safe = getSafeAreaInsets();
  if (targetRect.top < safe.top - CORNER_INSET_PX) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const minLeft = safe.left + EDGE_GAP_PX;
  const minTop = safe.top + EDGE_GAP_PX;
  const maxLeft = vw - safe.right - EDGE_GAP_PX - size.width;
  const maxTop = vh - safe.bottom - EDGE_GAP_PX - size.height;

  if (maxLeft < minLeft || maxTop < minTop) return null;

  const fits = (left: number, top: number) =>
    left >= minLeft && left <= maxLeft && top >= minTop && top <= maxTop;

  const outside: HintBeaconPosition[] = [
    // 1) Above-right — right edges aligned, fully clear of the CTA label.
    {
      top: clamped.top - size.height - OUTSIDE_GAP_PX,
      left: clamped.right - size.width,
    },
    // 2) Right of target — vertically near the top edge.
    {
      top: clamped.top + CORNER_INSET_PX,
      left: clamped.right + OUTSIDE_GAP_PX,
    },
    // 3) Below-right — for full-width rows with a control right above.
    {
      top: clamped.bottom + OUTSIDE_GAP_PX,
      left: clamped.right - size.width,
    },
  ].filter((pos) => fits(pos.left, pos.top));

  // 4) Inside top-right (TourKit corner) when the target cannot host outside.
  let left = clamped.right - CORNER_INSET_PX - size.width;
  let top = clamped.top + CORNER_INSET_PX;

  if (clamped.width >= size.width + CORNER_INSET_PX * 2) {
    left = Math.max(left, clamped.left + CORNER_INSET_PX);
  }
  if (clamped.height >= size.height + CORNER_INSET_PX * 2) {
    top = Math.min(top, clamped.bottom - CORNER_INSET_PX - size.height);
  }

  left = Math.min(Math.max(left, minLeft), maxLeft);
  top = Math.min(Math.max(top, minTop), maxTop);

  const candidates = [...outside, { top, left }];
  const free = isBlocked
    ? candidates.find(
        (pos) =>
          !isBlocked(new DOMRect(pos.left, pos.top, size.width, size.height)),
      )
    : undefined;
  return free ?? candidates[0]!;
}

export const HINT_BEACON_DEFAULT_SIZE: HintBeaconSize = {
  width: DEFAULT_BEACON_WIDTH_PX,
  height: DEFAULT_BEACON_HEIGHT_PX,
};
